/**
 * A nudge towards "mark for review", at the one moment it is useful.
 *
 * Marking for review is the feature students never use and most need. The
 * palette has a colour for it, the button is right there, and it stays unused
 * — because nobody reads a toolbar, they answer the question and move on.
 *
 * So the prompt appears only in the situation it is actually about: the
 * student **took a while** on this question and **then answered it**. Time
 * alone is not the signal (a long question takes long to read) and neither is
 * answering (most answers are confident). The pair is: this one was hard, and
 * you have committed to something. That is exactly when coming back later pays.
 *
 * Rules that keep it a hint rather than nagging:
 *
 *  - **Once per question.** It never reappears for one they have already seen
 *    it on, whatever they do afterwards.
 *  - **Never when already marked.** Suggesting something they have done reads
 *    as the app not paying attention.
 *  - **It fades on its own** and can be dismissed. It must never be a thing to
 *    deal with under exam pressure.
 */
import { useEffect, useRef, useState } from "react";
import { Flag, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Long enough that the question was genuinely worked at, rather than read.
 * Below this a student is answering from recognition, and there is nothing to
 * come back to.
 */
export const HESITATED_AFTER_MS = 45_000;

/** How long the hint stays before fading, if it is ignored. */
const VISIBLE_MS = 9_000;

export function ReviewHint({
  questionId,
  answered,
  markedForReview,
  msOnQuestion,
  onMark,
}: {
  questionId: string | null;
  answered: boolean;
  markedForReview: boolean;
  /** Time spent on this visit, from the runner's own clock. */
  msOnQuestion: number;
  onMark: () => void;
}) {
  const [showing, setShowing] = useState(false);
  /** Questions this has already fired for; it never fires twice for one. */
  const seen = useRef(new Set<string>());

  useEffect(() => {
    if (!questionId) return;
    if (!answered || markedForReview) return;
    if (msOnQuestion < HESITATED_AFTER_MS) return;
    if (seen.current.has(questionId)) return;

    seen.current.add(questionId);
    setShowing(true);
  }, [questionId, answered, markedForReview, msOnQuestion]);

  // Hide immediately if they act on it, or move on, or mark it themselves.
  useEffect(() => {
    setShowing(false);
  }, [questionId]);

  useEffect(() => {
    if (markedForReview) setShowing(false);
  }, [markedForReview]);

  useEffect(() => {
    if (!showing) return;
    const timer = setTimeout(() => setShowing(false), VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [showing]);

  if (!showing) return null;

  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2 rounded-md border border-accent/40 bg-accent-soft px-3 py-2",
        "animate-in fade-in duration-200",
      )}
    >
      <Flag className="mt-0.5 size-3.5 shrink-0 text-accent" aria-hidden />
      <p className="min-w-0 flex-1 text-2xs text-accent">
        That one took a while. If you are not certain,{" "}
        <button
          type="button"
          onClick={() => {
            setShowing(false);
            onMark();
          }}
          className="font-medium underline underline-offset-2 hover:no-underline"
        >
          mark it for review
        </button>{" "}
        — you can come back to it before you submit.
      </p>
      <button
        type="button"
        onClick={() => setShowing(false)}
        aria-label="Dismiss"
        className="shrink-0 rounded-sm p-0.5 text-accent/70 hover:bg-accent/10 hover:text-accent"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}
